import classNames from 'classnames/bind';
import React from 'react';
import { Field, Text, useSitecoreContext } from '@sitecore-jss/sitecore-jss-nextjs';
import Icon, { IconVariant } from 'components/helpers/Icon';

import styles from './Combobox.module.scss';

/** Properties interface of the combobox. */
export interface ComboboxProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /**
   * Identifies the currently active element within an associated listbox.
   * @see [aria-activedescendant](https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Attributes/aria-activedescendant)
   */
  ariaActiveDescendant?: React.AriaAttributes['aria-activedescendant'];

  /**
   * Identifies the element(s) controlled by the combobox.
   * @see [aria-controls](https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Attributes/aria-controls)
   */
  ariaControls?: React.AriaAttributes['aria-controls'];

  /**
   * Indicates that the combobox is expanded or collapsed.
   * @see [aria-expanded](https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Attributes/aria-expanded)
   * @defaultValue `false`
   */
  ariaExpanded?: React.AriaAttributes['aria-expanded'];

  /**
   * Indicates the type of popup element triggered by the combobox.
   * @see [aria-haspopup](https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Attributes/aria-haspopup)
   */
  ariaHasPopup?: React.AriaAttributes['aria-haspopup'];

  /**
   * Identifies the element(s) in order to define a contextual relationship.
   * @see [aria-owns](https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Attributes/aria-owns)
   */
  ariaOwns?: React.AriaAttributes['aria-owns'];

  /** Custom classname of the combobox. */
  className?: string;

  /**
   * Indicates that the combobox is disabled.
   * @defaultValue `false`
   */
  disabled?: boolean;

  /** Icon displayed next to the combobox. */
  icon: IconVariant;

  /** Unique identifier of the combobox. */
  id: string;

  /**
   * Callback method for when the combobox loses focus.
   * @param e - A focus event.
   */
  onBlur?: React.FocusEventHandler<HTMLButtonElement>;

  /**
   * Callback method for when the combobox is clicked.
   * @param e - A mouse event.
   */
  onClick?: React.MouseEventHandler<HTMLButtonElement>;

  /**
   * Callback method for when the combobox value changes.
   * @param e - A change event.
   */
  onChange?: React.ChangeEventHandler<HTMLButtonElement>;

  /**
   * Callback method for when a key is pressed while the combobox has focus.
   * @param e - A keyboard event.
   */
  onKeyDown?: React.KeyboardEventHandler<HTMLButtonElement>;

  /**
   * Callback method for when the mouse is held down on the combobox.
   * @param e - A mouse event.
   */
  onMouseDown?: React.MouseEventHandler<HTMLButtonElement>;

  /**
   * Placeholder text displayed when the combobox has no value.
   * @defaultValue `'Select...'`
   */
  placeholder?: string;

  /** Value to be submitted to a form. */
  value: string;
}

/** Default properties of the combobox. */
const defaultProps: Partial<ComboboxProps> = {
  ariaExpanded: false,
  disabled: false,
  icon: 'carat',
  placeholder: 'Select...',
};

/**
 * Classnames binding for the combobox.
 * @see [Classnames Usage](https://github.com/JedWatson/classnames#alternate-bind-version-for-css-modules)
 */
const cx = classNames.bind(styles);

/**
 * A combobox helper component.
 *
 * @see [W3C Combobox Pattern](https://www.w3.org/WAI/ARIA/apg/patterns/combobox/)
 *
 * @param props - Combobox properties.
 * @returns A JSX element.
 */
const Combobox = React.forwardRef<HTMLButtonElement, ComboboxProps>((props, ref) => {
  const {
    ariaActiveDescendant,
    ariaControls,
    ariaExpanded,
    ariaHasPopup,
    ariaOwns,
    className,
    disabled,
    icon,
    id,
    onBlur,
    onClick,
    onChange,
    onKeyDown,
    onMouseDown,
    placeholder,
    value,
  } = props;

  const { sitecoreContext } = useSitecoreContext();
  const classes = cx({ combobox: true }, className);
  let fieldValue: Field<string> = { value: value };
  if (value === '') {
    fieldValue = { value: placeholder ?? '' };
  }

  return (
    <div className={classes}>
      <button
        aria-activedescendant={ariaActiveDescendant}
        aria-controls={ariaControls}
        aria-expanded={ariaExpanded}
        aria-haspopup={ariaHasPopup}
        aria-owns={ariaOwns}
        className={cx({ button: true, selected: value })}
        data-value={value}
        disabled={disabled}
        id={id}
        onBlur={onBlur}
        onClick={onClick}
        onChange={onChange}
        onKeyDown={onKeyDown}
        onMouseDown={onMouseDown}
        ref={ref}
        role="combobox"
        tabIndex={disabled ? -1 : 0}
        type="button"
      >
        <Text className={cx({ text: true })} field={fieldValue} encode={sitecoreContext.pageEditing} />
        <Icon ariaHidden={true} className={cx({ icon: true })} variant={icon} />
      </button>
    </div>
  );
});

Combobox.defaultProps = defaultProps;
Combobox.displayName = 'Combobox';

export default Combobox;
